
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ResendVerificationButton } from './AdminBusinessActions';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Edit, Trash2, Eye } from 'lucide-react';
import { API } from '../../utils/api';

export function AdminUsers({ headers, role }) {
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [bindings, setBindings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [authFilter, setAuthFilter] = useState('all');

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users?role=${role}`, { headers });
      setUsers(res.data.items || []);
    } catch (e) {
      toast.error('Failed to fetch users');
    }
  };

  const fetchBindings = async () => {
    try {
      const res = await axios.get(`${API}/admin/bindings`, { headers });
      setBindings(res.data.items || []);
    } catch (e) {
      console.error('Failed to fetch bindings', e);
    }
  };

  useEffect(() => { fetchUsers(); fetchBindings(); }, [role]);

  const filteredUsers = users.filter(u => 
    (authFilter === 'all' || u.supabase_auth === authFilter) && (
    (u.full_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.id || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.paymig_id || '').toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const missingAuthCount = users.filter(u => u.supabase_auth === 'missing').length;

  const AUTH_BADGE = {
    linked: { label: 'Linked', variant: 'secondary' },
    missing: { label: 'Missing', variant: 'destructive' },
    not_applicable: { label: 'N/A', variant: 'outline' },
  };

  const handleExport = () => {
    const csvContent = "data:text/csv;charset=utf-8," 
      + "Full Name,Email,Phone,Nickname,Swish Alias,UUID,Paymig ID,Supabase Auth\n"
      + filteredUsers.map(u => `${u.full_name},${u.email},${u.phone},${u.nickname},${u.swish_payee_alias || ''},${u.id},${u.paymig_id || ''},${u.supabase_auth || ''}`).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${role}_users.csv`);
    document.body.appendChild(link);
    link.click();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user? This will also delete all their bindings.')) return;
    try {
      await axios.delete(`${API}/admin/users/${id}`, { headers });
      toast.success('User deleted');
      fetchUsers();
      fetchBindings();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail?.supabase_error
        ? `Failed to delete Supabase Auth user — app user kept. ${detail.supabase_error}`
        : 'Failed to delete user');
    }
  };

  const handleDeleteBinding = async (id) => {
    if (!window.confirm('Delete this relationship?')) return;
    try {
      await axios.delete(`${API}/admin/bindings/${id}`, { headers });
      toast.success('Binding deleted');
      fetchBindings();
    } catch (e) {
      toast.error('Failed to delete binding');
    }
  };

  const startEdit = (user) => {
    setEditingUser(user.id);
    setEditForm({
      full_name: user.full_name,
      email: user.email,
      phone: user.phone,
      nickname: user.nickname,
      swish_payee_alias: user.swish_payee_alias  // display only; never sent
    });
  };

  const saveEdit = async (id) => {
    try {
      const { swish_payee_alias, ...payload } = editForm; // admins can't change Swish numbers
      await axios.put(`${API}/admin/users/${id}`, payload, { headers });
      toast.success('User updated');
      setEditingUser(null);
      fetchUsers();
    } catch (e) {
      toast.error('Failed to update user');
    }
  };

  const getUserBindings = (userId) => {
    return bindings.filter(b => b.parent_id === userId || b.mobpay_id === userId);
  };

  const formatDate = (value) => {
    if (!value) return '-';
    try {
      return new Date(value).toLocaleDateString();
    } catch {
      return value;
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredUsers.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredUsers.map(u => u.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">{role === 'parent' ? 'Payers' : 'Buddys'}</h2>
          {missingAuthCount > 0 && (
            <p className="text-sm text-muted-foreground" data-testid="missing-auth-count">
              {missingAuthCount} of {users.length} not in Supabase Auth
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <select
            value={authFilter}
            onChange={e => setAuthFilter(e.target.value)}
            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
            data-testid="supabase-auth-filter"
          >
            <option value="all">All auth states</option>
            <option value="linked">Supabase: linked</option>
            <option value="missing">Supabase: missing</option>
            <option value="not_applicable">Supabase: N/A</option>
          </select>
          <Input 
            placeholder="Search users..." 
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-64"
          />
          <Button variant="outline" onClick={handleExport}>Export CSV</Button>
        </div>
      </div>
      
      {editingUser && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Edit User</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Full Name</Label><Input value={editForm.full_name} onChange={e=>setEditForm({...editForm, full_name: e.target.value})} /></div>
              <div><Label>Email</Label><Input value={editForm.email} onChange={e=>setEditForm({...editForm, email: e.target.value})} /></div>
              <div><Label>Phone</Label><Input value={editForm.phone} onChange={e=>setEditForm({...editForm, phone: e.target.value})} /></div>
              <div><Label>Nickname</Label><Input value={editForm.nickname} onChange={e=>setEditForm({...editForm, nickname: e.target.value})} /></div>
              {role === 'parent' && (
                <div><Label>Swish (read-only)</Label><Input value={editForm.swish_payee_alias || '—'} disabled data-testid="admin-user-swish-readonly" /></div>
              )}
            </div>
            <div className="flex gap-2">
              <Button onClick={() => saveEdit(editingUser)}>Save</Button>
              <Button variant="secondary" onClick={() => setEditingUser(null)}>Cancel</Button>
              <ResendVerificationButton userId={editingUser} headers={headers} testId="admin-user-resend-verification" />
            </div>
          </CardContent>
        </Card>
      )}

      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="p-3">
                <Checkbox
                  checked={filteredUsers.length > 0 && selectedIds.length === filteredUsers.length}
                  onCheckedChange={toggleSelectAll}
                  data-testid="select-all-users"
                />
              </th>
                  <th className="p-3">Name</th>
                  <th className="p-3">UUID</th>
                  <th className="p-3">Paymig ID</th>
              <th className="p-3">Email</th>
              <th className="p-3">Phone</th>
              <th className="p-3">Swish</th>
              <th className="p-3">Sign Up</th>
              <th className="p-3">Last Seen</th>
              <th className="p-3">Supabase Auth</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(u => (
              <tr key={u.id} className="border-t">
                <td className="p-3">
                  <Checkbox
                    checked={selectedIds.includes(u.id)}
                    onCheckedChange={() => toggleSelect(u.id)}
                    data-testid={`select-user-${u.id}`}
                  />
                </td>
                <td className="p-3 font-medium">{u.full_name || '—'}</td>
                <td className="p-3 text-xs text-muted-foreground">{u.id}</td>
                <td className="p-3 text-xs text-muted-foreground">{u.paymig_id || '—'}</td>
                <td className="p-3">{u.email || '—'}</td>
                <td className="p-3">{u.phone || '—'}</td>
                <td className="p-3">{u.swish_payee_alias || '—'}</td>
                <td className="p-3">{formatDate(u.created_at)}</td>
                <td className="p-3">{formatDate(u.last_login_at)}</td>
                <td className="p-3">
                  {AUTH_BADGE[u.supabase_auth]
                    ? <Badge variant={AUTH_BADGE[u.supabase_auth].variant} data-testid={`supabase-auth-${u.id}`}>{AUTH_BADGE[u.supabase_auth].label}</Badge>
                    : '—'}
                </td>
                <td className="p-3">
                  <div className="flex justify-end gap-2">
                    <Button size="icon" variant="outline" onClick={() => startEdit(u)} data-testid={`edit-user-${u.id}`}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="outline" data-testid={`view-user-${u.id}`}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button size="icon" variant="destructive" onClick={() => handleDelete(u.id)} data-testid={`delete-user-${u.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
                {filteredUsers.length === 0 && (
              <tr>
                    <td className="p-4 text-center text-muted-foreground" colSpan={11}>No users found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
